import { ArrowRight, CalendarDays, CircleCheckBig, Receipt, ShoppingCart } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { useBills, useEvents, useShopping } from "@/lib/hooks";
import { EVENT_CATEGORIES } from "@/lib/meta";
import { fmtLongDay, money, relativeDay, todayISO } from "@/lib/format";
import { useNavigate } from "@/components/app-shell";
import { WhoDot } from "@/components/member-avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function TodayPage() {
  const { user, memberById } = useAuth();
  const today = todayISO();
  const navigate = useNavigate();

  const { events } = useEvents(today, today);
  const { bills } = useBills();
  const { lists, items } = useShopping();

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const unpaid = bills.filter((b) => !b.paid).sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  const remaining = unpaid.reduce((sum, b) => sum + b.amount, 0);
  const toBuy = items.filter((i) => !i.checked);

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm text-muted-foreground">{fmtLongDay(new Date())}</p>
        <h2 className="text-2xl font-bold">
          {greeting}
          {user ? `, ${user.name}` : ""} 👋
        </h2>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {/* Today's schedule */}
        <Card className="md:row-span-2">
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarDays className="size-4 text-muted-foreground" /> Today's schedule
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("calendar")}>
              Calendar <ArrowRight className="size-3.5" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {events.length === 0 && <Empty>Nothing planned today. Enjoy! 🌿</Empty>}
            {events
              .slice()
              .sort((a, b) => (a.time ?? "").localeCompare(b.time ?? ""))
              .map((e) => {
                const member = e.who === "both" ? null : memberById(e.who);
                return (
                  <div key={`${e.id}-${e.date}`} className="flex items-center gap-3 rounded-lg border p-3">
                    <div className="w-14 shrink-0 text-sm font-medium tabular-nums text-muted-foreground">
                      {e.allDay || !e.time ? "All day" : e.time}
                    </div>
                    <span className={`size-2 rounded-full ${EVENT_CATEGORIES[e.category].dot}`} />
                    <div className="min-w-0 flex-1 truncate font-medium">{e.title}</div>
                    <WhoDot both={e.who === "both"} color={member?.color} />
                  </div>
                );
              })}
          </CardContent>
        </Card>

        {/* Bills */}
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <Receipt className="size-4 text-muted-foreground" /> Bills to pay
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("bills")}>
              All <ArrowRight className="size-3.5" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {unpaid.length === 0 && (
              <Empty>
                <CircleCheckBig className="mr-1 inline size-4 text-emerald-500" /> All bills are paid.
              </Empty>
            )}
            {unpaid.slice(0, 4).map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate font-medium">{b.name}</span>
                <span className="flex items-center gap-2 whitespace-nowrap text-muted-foreground">
                  {money(b.amount)}
                  <span className="text-xs">· {relativeDay(b.dueDate)}</span>
                </span>
              </div>
            ))}
            {unpaid.length > 0 && (
              <div className="flex items-center justify-between border-t pt-2 text-sm font-semibold">
                <span>Remaining</span>
                <span>{money(remaining)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shopping */}
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2 text-base">
              <ShoppingCart className="size-4 text-muted-foreground" /> Shopping
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("shopping")}>
              Open <ArrowRight className="size-3.5" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {toBuy.length === 0 && <Empty>Nothing to buy right now.</Empty>}
            {lists.map((l) => {
              const count = toBuy.filter((i) => i.listId === l.id).length;
              if (count === 0) return null;
              return (
                <div key={l.id} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{l.name}</span>
                  <span className="text-muted-foreground">{count} item{count === 1 ? "" : "s"}</span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-2 text-sm text-muted-foreground">{children}</p>;
}
